import type React from "react";
import { useState, useEffect, useRef } from "react";

import { sendEmail } from "@/app/(authenticated)/communication/_modules/services/sendEmail";
import type { SendEmailRequest, EmailAttachment } from "@/app/(authenticated)/communication/_modules/types/sendEmail.types";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Badge } from "@/app/_module/components/ui/badge";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";
import type { EmailOAuthConnection } from "@/app/_module/types/email-oauth";
import { Mail, AlertCircle, Loader2, Paperclip, X } from "lucide-react";

interface SendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead; // The lead object from the lead id page
}

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB total for all attachments

export const SendEmailModal: React.FC<SendEmailModalProps> = function({
  open,
  onOpenChange,
  lead,
}) {
  const [email, setEmail] = useState<string>(lead.email || "");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [oauthConnection, setOauthConnection] = useState<EmailOAuthConnection | null>(null);
  const [error, setError] = useState<string>("");
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update email if lead changes
  useEffect(() => {
    setEmail(lead.email || "");
  }, [lead]);

  // Check OAuth connection status when modal opens
  useEffect(() => {
    if (open) {
      checkOAuthConnection();
    }
  }, [open]);

  const checkOAuthConnection = async () => {
    setCheckingConnection(true);
    setError("");
    try {
      const status = await oauthConnectionService.getConnectionStatus();

      // Check if either Google or Microsoft is connected
      const googleConnected = status.google?.status === "connected";
      const microsoftConnected = status.microsoft?.status === "connected";

      if (googleConnected) {
        setOauthConnection(status.google!);
      } else if (microsoftConnected) {
        setOauthConnection(status.microsoft!);
      } else {
        setError("No email account connected. Please connect your Gmail or Microsoft account in Settings → Account to send emails.");
      }
    } catch (err) {

      setError("Failed to check email connection status. Please try again.");
    } finally {
      setCheckingConnection(false);
    }
  };

  // Convert File to base64 EmailAttachment
  const fileToAttachment = (file: File): Promise<EmailAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({
          filename: file.name,
          content: base64,
          contentType: file.type || 'application/octet-stream',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Calculate current total size
    const currentSize = attachmentFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      // Check individual file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" is too large. Max size is 10MB.`);
        continue;
      }

      // Check total size
      if (currentSize + file.size > MAX_TOTAL_SIZE) {
        setError("Total attachment size exceeds 25MB limit.");
        break;
      }

      // Check for duplicates
      if (attachmentFiles.some(f => f.name === file.name)) {
        continue;
      }

      try {
        const attachment = await fileToAttachment(file);
        setAttachments(prev => [...prev, attachment]);
        setAttachmentFiles(prev => [...prev, file]);
      } catch {
        setError(`Failed to read file "${file.name}".`);
      }
    }

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove an attachment
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSend = async () => {
    if (!email || !subject || !body || !oauthConnection) return;

    setLoading(true);
    setError("");

    const payload: SendEmailRequest = {
      to: email,
      subject,
      body,
      useOAuth: true, // Always use OAuth
      provider: oauthConnection.provider,
      leadId: lead.id, // Associate with lead for email history
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    try {
      await sendEmail(payload);
      setSubject("");
      setBody("");
      setAttachments([]);
      setAttachmentFiles([]);
      onOpenChange(false);
    } catch (err) {

      setError("Failed to send email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <ResponsiveDialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Send Email to {lead.firstName} {lead.lastName}</DialogTitle>
          <DialogDescription>
            {oauthConnection && !checkingConnection && (
              <div className="flex items-center gap-2 mt-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Sending from: <span className="font-medium">{oauthConnection.email}</span>
                </span>
                <Badge variant="outline" className="ml-auto">
                  {oauthConnection.provider === "google" ? "Gmail" : "Microsoft"}
                </Badge>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {checkingConnection ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">To</label>
                <Input
                  disabled // email is always from the lead, not editable
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Recipient email"
                  value={email}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Subject</label>
                <Input
                  required
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject"
                  value={subject}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Message</label>
                <Textarea
                  required
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your message..."
                  rows={8}
                  value={body}
                  disabled={loading}
                  className="resize-none"
                />
              </div>

              {/* Attachments Section */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium">Attachments</label>
                  <span className="text-xs text-muted-foreground">
                    Max 10MB per file, 25MB total
                  </span>
                </div>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                  disabled={loading}
                />

                {/* Attachment list */}
                {attachmentFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {attachmentFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between p-2 bg-muted rounded-md"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 shrink-0"
                          onClick={() => removeAttachment(index)}
                          disabled={loading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add attachment button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="w-full"
                >
                  <Paperclip className="mr-2 h-4 w-4" />
                  {attachmentFiles.length > 0 ? "Add more files" : "Attach files"}
                </Button>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading || !email || !subject || !body || !oauthConnection}
                onClick={handleSend}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                    {attachmentFiles.length > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {attachmentFiles.length} file{attachmentFiles.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </ResponsiveDialogContent>
    </Dialog>
  );
};